import { ASSET_URL } from "@api";
import { TranslationContext } from "@contexts";
import { MyRequestsNavLabel } from "@features/my-requests/components/MyRequestsNavLabel";
import { useHasMyRequestsNavActivity } from "@features/my-requests/useHasMyRequestsNavActivity";
import { useUnreadReceivedOrders } from "@features/my-requests/useUnreadReceivedOrders";
import { useQueryAppConfiguration } from "@hooks/appConfiguration/useQueryAppConfiguration";
import { useMarketplaceIdentity } from "@hooks/useMarketplaceIdentity";
import { useScrollToTop } from "@hooks/useScrollToTop";
import { Icon } from "@iconify/react";
import { useKeycloak } from "@react-keycloak/web";
import { memo, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { handleNonValidImg } from "../../helpers";
import { DropDownMenu } from "../DropDownMenu";
import { CartIconComp } from "./CartIconComp";
import { DropDownProfileOption } from "./DropDownProfileOption";
import { dropDownLanguages, mainMenuItems } from "./helpers";
import { LanguageMenuDropDownButton } from "./LanguageMenuDropDownButton";
import { MainMenuModal } from "./MainMenuModal";
import { MarketplaceNavLinks } from "./MarketplaceNavLinks";

export const NavBar = memo(() => {
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const { data: configObj, isLoading } = useQueryAppConfiguration();

  const { t } = useContext(TranslationContext);

  const location = useLocation();
  const navigate = useNavigate();

  const { scrollToTop } = useScrollToTop();

  const { i18n } = useContext(TranslationContext);

  const [showModal, setShowModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak.authenticated;
  const isSuperAdmin = keycloak.hasRealmRole("super-admin");
  const isAdmin = keycloak.hasRealmRole("admin");

  const identity = useMarketplaceIdentity();
  const hasMyRequestsNavActivity = useHasMyRequestsNavActivity();
  const { hasUnread: hasUnreadMyRequests } = useUnreadReceivedOrders();

  const myRequestsMenuTitle = (
    <MyRequestsNavLabel showNotification={hasUnreadMyRequests}>
      {t("myRequests")}
    </MyRequestsNavLabel>
  );

  const profileMenuHeader = identity.displayName
    ? t("profileGreeting", { name: identity.displayName })
    : t("profileGreetingGeneric");

  function changeLanguage(lang) {
    i18n.changeLanguage(lang);
    setShowLanguageMenu(false);
  }

  const logout = () => {
    keycloak.logout({ redirectUri: `${window.location.origin}/` });
  };

  function checkIfActive(path) {
    return location.pathname === path;
  }

  useEffect(() => {
    document.title = configObj.tabTitle || "-";
  }, [configObj.tabTitle]);

  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = configObj.favicon
        ? `${ASSET_URL}/${configObj.favicon}`
        : "empty_favicon.png";
    }
  }, [configObj.favicon]);

  const navbarBackgroundColor = configObj.navbarBackgroundColor ?? "#1E3A8A";
  const navbarTextColor = configObj.navbarTextColor ?? "white";

  return isLoading ? null : (
    <header
      className={[
        "NavBar",
        "fixed top-0 left-0",
        "w-screen max-w-screen h-16",
        "grid grid-cols-[max-content_max-content]",
        "justify-between gap-2 items-center",
        "px-4 shadow-md z-[100]",
        "xl:pr-8",
      ].join(" ")}
      style={{ backgroundColor: navbarBackgroundColor }}
      data-testid="navBar"
    >
      <Link
        to="/"
        className="flex items-center gap-2 justify-self-start text-xl font-bold xl:text-2xl xl:gap-4"
        style={{ color: navbarTextColor }}
        onClick={scrollToTop}
      >
        {configObj.appName}

        <img
          src={
            configObj.appLogoSrc ? `${ASSET_URL}/${configObj.appLogoSrc}` : ""
          }
          alt={`${configObj.appName} logo`}
          className="h-8 xl:h-10"
          onError={handleNonValidImg}
          data-testid="appLogo"
        />
      </Link>
      {isAuthenticated ? (
        <>
          <Icon
            icon="heroicons-solid:menu"
            className="text-2xl text-white xl:hidden justify-self-end"
            onClick={() => setShowModal(true)}
          />
          <nav
            className="hidden xl:grid place-items-center grid-flow-col gap-5 justify-self-end"
            data-testid="navBarContent"
          >
            {configObj.hasMultiLanguage && (
              <DropDownMenu
                items={dropDownLanguages}
                onItemClicked={(lang) => changeLanguage(lang)}
                MenuButton={
                  <LanguageMenuDropDownButton
                    textColor={configObj.navbarTextColor}
                  />
                }
                showMenu={showLanguageMenu}
                setShowMenu={setShowLanguageMenu}
              />
            )}
            {(isSuperAdmin || isAdmin) && (
              <Link
                to="/back-office"
                className={`grid grid-cols-[max-content_max-content] gap-0.5 items-center border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                  checkIfActive("/back-office") ? "text-amber-500" : ""
                }`}
                style={
                  !checkIfActive("/back-office")
                    ? { color: navbarTextColor }
                    : undefined
                }
              >
                {t("backOffice")}
              </Link>
            )}
            {configObj.showAbout && (
              <Link
                to="/about"
                className={`grid grid-cols-[max-content_max-content] gap-0.5 items-center border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                  checkIfActive("/about") ? "text-amber-500" : ""
                }`}
                style={
                  !checkIfActive("/about")
                    ? { color: navbarTextColor }
                    : undefined
                }
                data-testid="aboutNav"
              >
                {t("about")}
              </Link>
            )}
            <MarketplaceNavLinks
              t={t}
              checkIfActive={checkIfActive}
              navbarTextColor={navbarTextColor}
              showMyRequestsNav={hasMyRequestsNavActivity}
              showMyRequestsUnread={hasUnreadMyRequests}
              navbarBackgroundColor={navbarBackgroundColor}
              showMyAssets={identity.showSellerNav}
            />
            {configObj.hasCart && (
              <Link
                to="/cart"
                className={`grid grid-cols-[max-content_max-content] gap-0.5 items-center border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
                  checkIfActive("/cart") ? "text-amber-500" : ""
                }`}
                style={
                  !checkIfActive("/cart")
                    ? { color: navbarTextColor }
                    : undefined
                }
              >
                <CartIconComp />
              </Link>
            )}
            <DropDownMenu
              items={mainMenuItems({
                myRequestsTitle: myRequestsMenuTitle,
                lockMyAssets: !identity.showSellerNav,
              })}
              menuHeader={profileMenuHeader}
              MenuButton={<DropDownProfileOption />}
              menuButtonAriaLabel={t("profile")}
              onItemClicked={(path) => navigate(path)}
              showMenu={showProfileMenu}
              setShowMenu={setShowProfileMenu}
              lastOptionLabel={t("logout")}
              lastOptionFn={logout}
              menuItemsPosition="left"
              textColor={configObj.navbarTextColor}
              dataTestId="navDropDownMenu"
            />
          </nav>
        </>
      ) : (
        <nav
          className="flex items-center gap-5 justify-self-end"
          data-testid="guestNav"
        >
          <Link
            to="/products"
            className={`border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none ${
              checkIfActive("/products") ? "text-amber-500" : ""
            }`}
            style={
              !checkIfActive("/products")
                ? { color: navbarTextColor }
                : undefined
            }
            data-testid="guestAllProductsNav"
          >
            {t("allProducts")}
          </Link>
          <Link
            to="/login?mode=register"
            className="border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none"
            style={{ color: navbarTextColor }}
            data-testid="signUpBtn"
          >
            {t("signUp")}
          </Link>
          <Link
            to="/login"
            className="border-none bg-none cursor-pointer font-medium transition-all duration-200 ease-in-out hover:scale-110 hover:text-[#f2987a] focus:text-[#f2987a] focus:outline-none"
            style={{ color: navbarTextColor }}
            data-testid="loginBtn"
          >
            {t("login")}
          </Link>
        </nav>
      )}
      {showModal && (
        <MainMenuModal setShowModal={setShowModal} />
      )}
    </header>
  );
});
